import moment from 'moment';

import type { TScheduleTemplate } from '@components/schedule/common/types';

/** Часы, которые показываем по вертикали, когда графика компании нет */
const FALLBACK_HOURS = { from: 8, to: 20 };

/**
 * Поля графика, по которым строится сетка. Им отвечают и график организации
 * (`TSchedule`), и шаблон (`TScheduleTemplate`) — модалке всё равно, что передали.
 */
export type TWorkSchedule = Pick<
    TScheduleTemplate,
    'start_time' | 'end_time' | 'breaks' | 'day_offs' | 'schedule_type'
>;

/** Полуинтервал часов `[from, to)` — строки сетки календаря */
export type THourRange = { from: number; to: number };

const toMinutes = (time: string) => {
    const [hours, minutes] = time.split(':').map(Number);
    return hours * 60 + (minutes || 0);
};

/**
 * Рабочие часы компании по графику: от часа начала до часа окончания дня
 * включительно — в строку «17:00» ставят окончание записи при графике до 17:00.
 * Без графика — запасной диапазон.
 */
export const getWorkingHourRange = (schedule?: TWorkSchedule | null): THourRange => {
    if (!schedule?.start_time || !schedule?.end_time) return FALLBACK_HOURS;

    return {
        from: Math.floor(toMinutes(schedule.start_time) / 60),
        to: Math.floor(toMinutes(schedule.end_time) / 60) + 1,
    };
};

/**
 * Расширяет видимые часы так, чтобы в них попали уже выбранные даты: запись,
 * поставленная вне графика, иначе пропала бы из сетки.
 */
export const includeHours = (range: THourRange, dates: Array<moment.Moment | null>) =>
    dates.reduce<THourRange>((acc, date) => {
        if (!date) return acc;
        return { from: Math.min(acc.from, date.hour()), to: Math.max(acc.to, date.hour() + 1) };
    }, range);

/**
 * Рабочий ли час по графику компании: день не выходной и час целиком внутри
 * рабочего интервала, мимо перерывов. `day_offs` бэк хранит в ISO (1 = пн … 7 = вс).
 *
 * Учитываются только графики 5/2 и ежедневный: для N/M и дней-исключений формат
 * отсчёта не описан — такие дни считаем рабочими по общему интервалу.
 */
export const isWorkingHour = (schedule: TWorkSchedule | null | undefined, cell: moment.Moment) => {
    if (!schedule?.start_time || !schedule?.end_time) return false;

    const isDayOff = schedule.schedule_type === 0 && schedule.day_offs?.includes(cell.isoWeekday());
    if (isDayOff) return false;

    const cellStart = cell.hour() * 60;
    const cellEnd = cellStart + 60;
    const isInsideWorkday =
        cellStart >= toMinutes(schedule.start_time) && cellEnd <= toMinutes(schedule.end_time);
    if (!isInsideWorkday) return false;

    return !(schedule.breaks ?? []).some(
        (item) =>
            cellStart < toMinutes(item.end_break_time) && cellEnd > toMinutes(item.start_break_time)
    );
};
